import React from 'react'
import { Button } from '@/components/ui/button'
import { HomeButtons } from '@/components/HomeButtons'
import { Features } from '@/components/Features'
import { Pricing } from '@/components/Pricing'
import Head from 'expo-router/head'
import { Footer } from '@/components/Footer'
import { GettingStartedPublic } from '@/components/GettingStartedPublic'
import { PaymentMeta } from '@/constants/PaymentMeta'
import { Testimonials } from '@/components/Testimonials'
import { Comparison } from '@/components/Comparison'

export const unstable_settings = {
  // This component will be rendered at build-time and never re-rendered in production.
  render: 'static',
}

export default function LandingPage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'React Native Google Sign In',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'iOS, Android, Web, MacOS',
    description:
      'Universal Google Sign-In for React Native. One package for Android, iOS, Web, and macOS built on the latest stable Google identity APIs, with easier setup tooling and advanced security features.',
    url: 'https://universal-sign-in.com',
    offers: [
      {
        '@type': 'Offer',
        name: 'Personal License',
        price: PaymentMeta.personal.cost,
        priceCurrency: 'USD',
        priceValidUntil: '2026-12-31',
        availability: 'https://schema.org/InStock',
        validFrom: '2025-01-01',
      },
      {
        '@type': 'Offer',
        name: 'Team License',
        price: PaymentMeta.team.cost,
        priceCurrency: 'USD',
        priceValidUntil: '2026-12-31',
        availability: 'https://schema.org/InStock',
        validFrom: '2025-01-01',
      },
    ],
  }

  return (
    <>
      <Head>
        <title>Universal Google Sign-In for React Native</title>
        <meta
          name="description"
          content="Universal Google Sign-In for React Native: one package for Android, iOS, Web, and macOS. Credential Manager support on Android, easier setup tooling, and advanced security features."
        />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>
      <div className="">
        <header
          className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/90"
          role="banner"
        >
          <div className="container mx-auto px-4 py-4">
            <nav
              className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:items-center"
              role="navigation"
              aria-label="Main navigation"
            >
              <div className="flex items-center gap-6 overflow-x-auto whitespace-nowrap sm:overflow-visible">
                <a href="#pricing" className="text-foreground hover:text-primary transition-colors">
                  Pricing
                </a>
                <a
                  href="#testimonials"
                  className="text-foreground hover:text-primary transition-colors"
                >
                  Testimonials
                </a>
                <a
                  href="#features"
                  className="text-foreground hover:text-primary transition-colors"
                >
                  Features
                </a>
                <a href="#compare" className="text-foreground hover:text-primary transition-colors">
                  Compare
                </a>
                <a
                  href="https://github.com/react-native-google-signin/google-signin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center px-3 py-2 rounded-lg transition-colors align-middle"
                  aria-label="GitHub stars for react-native-google-signin/google-signin"
                >
                  <img
                    src="https://img.shields.io/github/stars/react-native-google-signin/google-signin?style=social"
                    alt="GitHub stars"
                    className="h-5"
                  />
                </a>
              </div>

              <div className="flex items-center justify-center gap-3 sm:justify-end sm:gap-4">
                <a href="#pricing">
                  <Button
                    variant="outline"
                    className="h-9 px-3 text-sm sm:h-10 sm:px-4 sm:text-sm sm:hover:scale-105 transform sm:duration-200"
                  >
                    Get a license
                  </Button>
                </a>
                <a href="/auth/login">
                  <Button
                    variant="outline"
                    className="h-9 px-3 text-sm sm:h-10 sm:px-4 sm:text-sm sm:hover:scale-105 transform sm:duration-200"
                  >
                    Sign in
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        </header>

        <main className="container mx-auto px-4 py-12 overflow-y-auto" role="main">
          <section className="text-center" aria-labelledby="hero-heading">
            <h1
              id="hero-heading"
              className="text-5xl font-bold mb-6 text-foreground leading-tight py-1"
            >
              Universal Google Sign-In for React Native
            </h1>
            <p className="text-lg text-muted-foreground mb-0 max-w-2xl sm:max-w-3xl lg:max-w-4xl mx-auto">
              Full-featured Google Sign-In with Config Doctor for fast integration, maintained for
              production reliability and compatibility across Expo/RN versions.
            </p>
          </section>

          <section className="mb-16 text-center" aria-labelledby="pain-point-heading">
            <h2 id="pain-point-heading" className="sr-only">
              React Native maintenance pain point
            </h2>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-lg font-semibold mb-4 mt-8 text-foreground">
                Unmaintained packages are the{' '}
                <span className="text-primary dark:text-white">#1 pain point</span> in React Native
                development
                <span className="align-super text-xs text-muted-foreground" aria-hidden="true">
                  *
                </span>
              </p>
              <p className="text-lg text-muted-foreground mb-2">
                Your purchase keeps the package rock-solid and up-to-date with new Expo & React
                Native releases — so you can avoid the headaches of abandoned dependencies.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                * According to the{' '}
                <a
                  href="https://results.2025.stateofreactnative.com/en-US/opinions/#opinions_pain_points_multiple"
                  target="_blank"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                  rel="noopener noreferrer"
                >
                  2025
                </a>{' '}
                &{' '}
                <a
                  href="https://results.2024.stateofreactnative.com/en-US/opinions/#opinions_pain_points_multiple"
                  target="_blank"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                  rel="noopener noreferrer"
                >
                  2024
                </a>{' '}
                State of React Native Survey
              </p>
            </div>
            <div className="my-8 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <HomeButtons showPricingCta={false} />
            </div>
          </section>

          <Pricing />
          <Testimonials />
          <Features />
          <Comparison />
          <GettingStartedPublic />

          <section className="text-center" aria-labelledby="cta-heading">
            <h2 id="cta-heading" className="text-2xl font-semibold text-foreground mb-2">
              Ready to ship?
            </h2>
            <p className="text-muted-foreground mb-6">
              Get your license and add Google Sign-In to your app today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <HomeButtons showPricingCta pricingCtaText="Get a license" />
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  )
}
